import { useState, useEffect } from "react";
import { BarChart2 } from "lucide-react";
import PageHeader from '../components/PageHeader';
import LoadingDots from '../components/LoadingDots';
import MispricedStockList from '../components/MispricedStockList';

export default function MispricedPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/mispriced")
      .then(r => r.json())
      .then(d => {
        setData(d);
      })
      .catch(() => setData({ undervalued:[], overvalued:[], generatedAt:null }))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasData = data && (data.undervalued?.length > 0 || data.overvalued?.length > 0);

  function updatedLabel() {
    if (!data?.generatedAt) return null;
    const days = Math.floor((Date.now() - new Date(data.generatedAt)) / 86400000);
    if (days === 0) return "Updated today";
    if (days === 1) return "Updated yesterday";
    return `Updated ${days} days ago`;
  }

  return (
    <div style={{ fontFamily:"'Inter',sans-serif", background:"#000", minHeight:"100vh", color:"#fff" }}>
      <style>{"@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@700;900&family=Rajdhani:wght@600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');html,body{background:#000!important;color:#fff}*{box-sizing:border-box}a,button{font-family:inherit;cursor:pointer}"}</style>
      <PageHeader active="mispriced"/>
      <div className="hma-main-pad" style={{ maxWidth:1440, margin:"0 auto", padding:"28px 32px 60px" }}>

        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:20 }}>
          <div>
            <div style={{ fontFamily:"'Cinzel',serif", fontSize:"clamp(28px,4vw,38px)", fontWeight:900, background:"linear-gradient(135deg,#ffe87a,#a07030)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", marginBottom:4 }}>Mispriced Stocks</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.78)" }}>20 most undervalued and 20 most overvalued large-cap stocks — updated weekly by our team.</div>
          </div>
          {data?.generatedAt && (
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:8, padding:"6px 12px", alignSelf:"center" }}>
              🕐 {updatedLabel()}
            </div>
          )}
        </div>

        {loading && <LoadingDots msg="Loading mispriced stocks…"/>}

        {!loading && !hasData && (
          <div style={{ background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:16, padding:"48px", textAlign:"center" }}>
            <div style={{ marginBottom:16, display:"flex", justifyContent:"center" }}><BarChart2 size={44} strokeWidth={1.5} color="rgba(201,168,76,0.5)"/></div>
            <div style={{ fontFamily:"'Cinzel',serif", fontSize:20, fontWeight:700, color:"#fff", marginBottom:10 }}>Data coming soon</div>
            <div style={{ fontSize:14, color:"rgba(255,255,255,0.78)", lineHeight:1.7 }}>Our team updates this list weekly. Check back soon.</div>
          </div>
        )}

        {!loading && hasData && (
          <>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:22 }}>
              {[
                { label:"Avg Undervalued Upside", val: data.undervalued?.length ? "+" + (data.undervalued.reduce((a,s)=>a+(parseFloat(s.percentUpside)||0),0)/data.undervalued.length).toFixed(1)+"%" : "—", color:"#00C805" },
                { label:"Avg Overvalued Downside", val: data.overvalued?.length ? "-" + (data.overvalued.reduce((a,s)=>a+(parseFloat(s.percentDownside)||0),0)/data.overvalued.length).toFixed(1)+"%" : "—", color:"#FF3B30" },
              ].map(s=>(
                <div key={s.label} style={{ background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:10, padding:"8px 16px" }}>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".1em", color:"rgba(255,255,255,0.3)", marginBottom:2 }}>{s.label}</div>
                  <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:20, fontWeight:800, color:s.color }}>{s.val}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", alignItems:"flex-start", gap:10, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"10px 14px", marginBottom:16 }}>
              <span style={{ fontSize:16, flexShrink:0 }}>ℹ️</span>
              <span style={{ fontSize:12, color:"rgba(255,255,255,0.78)", lineHeight:1.6 }}>
                <strong style={{ color:"rgba(255,255,255,0.7)" }}>This list is updated weekly</strong> using a broad AI scan. Tapping a stock runs a fresh, deeper analysis using live market data — so the grade or verdict may differ. Think of this list as a starting point, not the final word.
              </span>
            </div>
            <div className="hma-mispriced-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:22 }}>
              <MispricedStockList stocks={data.undervalued} type="under"/>
              <MispricedStockList stocks={data.overvalued} type="over"/>
            </div>
          </>
        )}

        <div style={{ fontSize:11, color:"rgba(255,255,255,0.22)", textAlign:"center", marginTop:24, lineHeight:1.65 }}>For educational purposes only. AI-sourced valuations may contain errors. Always verify before investing.</div>
      </div>
    </div>
  );
}
